import { Creditor } from '../types';

export const ANNUAL_INTEREST_RATE = 18.6;

const MONTHLY_LEGAL_FEE = 55;
const FIRST_MONTH_PROCESSING_FEE = 21.80;
const MONTHLY_PROCESSING_FEE = 12.80;
const ONE_TIME_ENROLLMENT_FEE = 240;

export const calculateMonthlyInterest = (totalBalance: number): number => {
  return (ANNUAL_INTEREST_RATE / 12 / 100) * totalBalance;
};

export const calculateAnnualInterest = (totalBalance: number): number => {
  return calculateMonthlyInterest(totalBalance) * 12;
};

export const calculateProposedPayment = (
  totalBalance: number,
  termMonths: number,
  estimatedSettlementRate: number = 0.50,
  companyFeeRate: number = 0.25
): number => {
  // Calculate base program amount (settlement + company fee)
  const settlementAmount = totalBalance * estimatedSettlementRate;
  const companyFee = totalBalance * companyFeeRate;
  const baseProgramAmount = settlementAmount + companyFee;
  
  // Calculate total monthly fees for the term
  const totalMonthlyFees = (MONTHLY_LEGAL_FEE + MONTHLY_PROCESSING_FEE) * termMonths;
  
  // Total program amount including all fees
  const totalProgramAmount = baseProgramAmount + totalMonthlyFees + ONE_TIME_ENROLLMENT_FEE;
  
  // Calculate monthly payment (rounded to nearest cent)
  return Math.ceil(totalProgramAmount / termMonths * 100) / 100;
};

export const calculateTotalSavings = (
  totalBalance: number,
  monthlyPayment: number,
  termMonths: number,
  currentMonthlyPayment: number
): number => {
  // Calculate total program cost
  const settlementAmount = totalBalance * 0.50; // 50% settlement
  const companyFee = totalBalance * 0.25; // 25% company fee
  const totalMonthlyFees = (MONTHLY_LEGAL_FEE + MONTHLY_PROCESSING_FEE) * termMonths;
  const totalProgramCost = settlementAmount + companyFee + totalMonthlyFees + ONE_TIME_ENROLLMENT_FEE;
  
  // Calculate what they would pay with current payments
  const totalCurrentPayments = totalBalance; // They would pay full balance without program
  
  // Savings is the difference between original balance and total program cost
  return totalCurrentPayments - totalProgramCost;
};

export const calculatePaymentBreakdown = (
  totalBalance: number,
  monthlyPayment: number
): { interestPayment: number; principalPayment: number } => {
  const monthlyInterest = calculateMonthlyInterest(totalBalance);
  return {
    interestPayment: monthlyInterest,
    principalPayment: monthlyPayment - monthlyInterest,
  };
};

interface PayoffResult {
  months: number;
  years: number;
  willNeverPayoff: boolean;
  totalInterestPaid: number;
  totalPaid: number;
}

export const calculatePayoffTime = (
  balance: number,
  monthlyPayment: number,
  monthlyInterestPayment: number,
  annualInterestRate: number
): PayoffResult => {
  // For fixed monthly payment of 1260 with 651 interest
  let currentBalance = balance;
  let months = 0;
  let totalInterestPaid = 0;
  const principalPayment = monthlyPayment - monthlyInterestPayment;

  // If principal payment is too small to make progress
  if (principalPayment <= 0) {
    return {
      months: 0,
      years: 0,
      willNeverPayoff: true,
      totalInterestPaid: 0,
      totalPaid: 0
    };
  }

  // Calculate months until payoff with fixed principal payment
  while (currentBalance > 0 && months < 1200) {
    currentBalance -= principalPayment;
    totalInterestPaid += monthlyInterestPayment;
    months++;
  }

  // Should be approximately 69 months (5 years and 9 months)
  return {
    months,
    years: Number((months / 12).toFixed(1)),
    willNeverPayoff: false,
    totalInterestPaid,
    totalPaid: monthlyPayment * months
  };
};

export const calculateScenario = (
  balance: number,
  monthlyPayment: number,
  monthlyInterestPayment: number,
  additionalPayment: number,
  annualInterestRate: number
): {
  originalPayoff: PayoffResult;
  newPayoff: PayoffResult;
  savings: number;
  timeSavedMonths: number;
} => {
  const original = calculatePayoffTime(
    balance,
    monthlyPayment,
    monthlyInterestPayment,
    annualInterestRate
  );

  const newPayment = monthlyPayment + additionalPayment;
  const new_ = calculatePayoffTime(
    balance,
    newPayment,
    monthlyInterestPayment,
    annualInterestRate
  );

  return {
    originalPayoff: original,
    newPayoff: new_,
    savings: original.totalPaid - new_.totalPaid,
    timeSavedMonths: original.months - new_.months
  };
};
